import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, playerApi, playerToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import GamePlayer from "@/components/games/GamePlayer";
import { getIcon, gameGradient, term } from "@/lib/games";
import { Home, Grid3x3, Trophy, Gift, Loader2, Zap, ScanLine, Lock, CheckCircle2, Clock, Award, Star, History, Coins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

export default function PlayerArena() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("home");
  const [zones, setZones] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [txns, setTxns] = useState([]);
  const [playing, setPlaying] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState("");

  const pApi = playerApi(slug);

  const loadPlayer = useCallback(async () => {
    try {
      const r = await pApi.get("/public/players/me");
      setPlayer(r.data.player); setEvent(r.data.event);
    } catch { playerToken.clear(slug); }
    setLoading(false);
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => {
    api.get(`/public/events/${slug}`).then((r) => setEvent(r.data)).catch(() => setNotFound(true));
    if (playerToken.get(slug)) loadPlayer(); else setLoading(false);
  }, [slug, loadPlayer]);

  const refresh = useCallback(() => {
    if (!playerToken.get(slug)) return;
    pApi.get(`/public/events/${slug}/zones`).then((r) => setZones(r.data)).catch(() => {});
    pApi.get("/public/players/me").then((r) => setPlayer(r.data.player)).catch(() => {});
    api.get(`/public/events/${slug}/leaderboard`).then((r) => setLeaderboard(r.data)).catch(() => {});
    pApi.get("/public/players/me/rewards").then((r) => setRewards(r.data)).catch(() => {});
    pApi.get("/public/players/me/achievements").then((r) => setAchievements(r.data)).catch(() => {});
    pApi.get("/public/players/me/transactions").then((r) => setTxns(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [slug]);

  useEffect(() => { if (player) refresh(); }, [player?.player_id, refresh]);

  useEffect(() => {
    const vc = new URLSearchParams(window.location.search).get("vc");
    if (vc && player) { setCode(vc.toUpperCase()); setScanOpen(true); }
  }, [player?.player_id]);

  if (notFound) return <div className="min-h-screen grid place-items-center text-muted-foreground">Arena not found</div>;
  if (loading || !event) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  if (!player) return <Register event={event} slug={slug} onDone={(p) => { setPlayer(p); }} />;

  const redeem = async (tok) => {
    const token = (tok || code).trim().toUpperCase();
    if (!token) { toast.error("Enter a code"); return; }
    try {
      const r = await pApi.post("/public/victory/redeem", { token });
      confetti({ particleCount: 160, spread: 90, origin: { y: 0.5 } });
      toast.success(`Victory! +${r.data.xp_earned} Cash`);
      setScanOpen(false); setScanning(false); setCode(""); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  const xpLabel = term(event, "xp", "Cash");
  const NAV = [
    { k: "home", i: Home, l: "Home" }, { k: "zones", i: Grid3x3, l: term(event, "zone", "Zones") },
    { k: "hof", i: Trophy, l: "Ranks" }, { k: "rewards", i: Gift, l: "Rewards" },
  ];

  return (
    <div className="min-h-screen pb-24 max-w-md mx-auto relative" style={{ "--pt": event.theme }}>
      {/* header */}
      <div className="p-5 pt-7" style={{ background: `linear-gradient(160deg, ${event.theme}22, transparent)` }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide">{event.name}</p>
            <h1 className="font-head font-black text-2xl">Hey, {player.name.split(" ")[0]} 👋</h1>
          </div>
          <Button size="sm" className="rounded-full font-bold brut-hard" onClick={() => setScanOpen(true)} data-testid="scan-victory-btn"><ScanLine className="w-4 h-4 mr-1" />Claim</Button>
        </div>
        <div className="brut-primary rounded-2xl p-5 bg-card animate-pop" data-testid="xp-wallet">
          <p className="text-sm text-muted-foreground">{term(event, "xp", "Cash")} Wallet</p>
          <div className="flex items-center gap-2 font-mono-num font-extrabold text-5xl" style={{ color: event.theme }}>
            <Coins className="w-8 h-8" strokeWidth={2.5} />{player.total_xp.toLocaleString()}
            <span className="text-base ml-1 text-muted-foreground self-end mb-1">{xpLabel}</span>
          </div>
        </div>
      </div>

      <div className="px-5 mt-4">
        {tab === "home" && <HomeTab event={event} zones={zones} onPlay={setPlaying} achievements={achievements} txns={txns} xpLabel={xpLabel} />}
        {tab === "zones" && <ZonesTab event={event} zones={zones} onPlay={setPlaying} />}
        {tab === "hof" && <HofTab event={event} leaderboard={leaderboard} playerId={player.player_id} slug={slug} />}
        {tab === "rewards" && <RewardsTab rewards={rewards} event={event} />}
      </div>

      {/* bottom nav */}
      <div className="fixed bottom-0 inset-x-0 max-w-md mx-auto glass border-t border-border grid grid-cols-4 z-40">
        {NAV.map((n) => { const I = n.i; return (
          <button key={n.k} onClick={() => setTab(n.k)} data-testid={`nav-${n.k}`}
            className={`py-3 flex flex-col items-center gap-1 text-xs ${tab === n.k ? "text-primary" : "text-muted-foreground"}`}>
            <I className="w-5 h-5" />{n.l}
          </button>
        ); })}
      </div>

      {playing && <GamePlayer slug={slug} zone={playing} zoneId={playing.zone_id} onClose={() => setPlaying(null)} onComplete={refresh} />}

      <Dialog open={scanOpen} onOpenChange={(o) => { setScanOpen(o); if (!o) setScanning(false); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-head">Claim a Victory Code</DialogTitle></DialogHeader>
          {scanning ? (
            <div className="space-y-3">
              <CameraScanner onResult={(t) => { setScanning(false); redeem(extractToken(t)); }} onCancel={() => setScanning(false)} />
              <Button variant="outline" className="w-full rounded-full" onClick={() => setScanning(false)} data-testid="scan-cancel">Cancel scan</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Point your camera at the Victory QR shown by the Game Master.</p>
              <Button className="w-full rounded-full font-bold" onClick={() => setScanning(true)} data-testid="scan-camera-btn"><ScanLine className="w-4 h-4 mr-1" />Scan QR with camera</Button>
              <p className="text-xs text-center text-muted-foreground">or enter the code manually</p>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="ENTER CODE" className="font-mono-num text-center text-lg uppercase" data-testid="victory-code-input" />
              <Button variant="secondary" className="w-full rounded-full font-bold" onClick={() => redeem()} data-testid="victory-redeem-btn">Claim Cash</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Register({ event, slug, onDone }) {
  const [form, setForm] = useState({});
  const [team, setTeam] = useState("");
  const [saving, setSaving] = useState(false);
  const [code, setCode] = useState("");
  const [resuming, setResuming] = useState(false);
  const fields = event.participant_fields || [];
  const submit = async () => {
    const name = form.name, contact = form.contact;
    if (!name || !contact) { toast.error("Please fill your name and contact"); return; }
    setSaving(true);
    try {
      const extra = {};
      fields.forEach((f) => { if (!f.system) extra[f.key] = form[f.key]; });
      const r = await api.post(`/public/events/${slug}/register`, {
        name, contact, participant_id: form.participant_id, team_id: team || null, extra,
      });
      playerToken.set(slug, r.data.player_token);
      confetti({ particleCount: 120, spread: 70 });
      onDone(r.data.player);
    } catch (e) { toast.error(e.response?.data?.detail || "Registration failed"); }
    finally { setSaving(false); }
  };
  const resume = async () => {
    if (!code.trim()) { toast.error("Enter your code"); return; }
    setResuming(true);
    try {
      const r = await api.post(`/public/events/${slug}/resume`, { code: code.trim() });
      playerToken.set(slug, r.data.player_token);
      confetti({ particleCount: 120, spread: 70 });
      onDone(r.data.player);
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
    finally { setResuming(false); }
  };
  return (
    <div className="min-h-screen max-w-md mx-auto p-6 flex flex-col justify-center">
      {event.cover_image_url && <img src={event.cover_image_url} className="w-full h-36 object-cover rounded-2xl mb-5" alt="" />}
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full text-xs uppercase tracking-wide mb-3" style={{ background: `${event.theme}22`, color: event.theme }}>{event.event_type}</div>
        <h1 className="font-head font-black text-3xl">Welcome to {event.name}</h1>
        <p className="text-muted-foreground mt-1">Register to start earning {term(event, "xp", "Cash")}.</p>
      </div>
      <div className="brut p-6 space-y-4">
        {fields.map((f) => (
          <div key={f.key}>
            <Label className="mb-1.5 block">{f.label}{f.required && " *"}</Label>
            <Input value={form[f.key] || ""} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))} data-testid={`reg-${f.key}`} />
          </div>
        ))}
        {event.teams_enabled && event.teams?.length > 0 && (
          <div>
            <Label className="mb-1.5 block">Pick your team</Label>
            <div className="flex flex-wrap gap-2">
              {event.teams.map((t) => (
                <button key={t.team_id} onClick={() => setTeam(t.team_id)} className={`px-3 py-2 rounded-full border-2 border-neutral-300 text-sm font-semibold ${team === t.team_id ? "text-white" : "bg-card"}`} style={team === t.team_id ? { background: t.color } : {}} data-testid={`team-${t.team_id}`}>{t.name}</button>
              ))}
            </div>
          </div>
        )}
        <Button className="w-full rounded-full font-bold h-12 brut-hard" onClick={submit} disabled={saving} data-testid="register-submit">
          {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enter Arena 🎮"}
        </Button>
      </div>
      <div className="brut p-5 mt-4">
        <p className="text-sm font-semibold mb-1">Already pre-booked?</p>
        <p className="text-xs text-muted-foreground mb-3">Enter the code your organizer gave you to jump straight in.</p>
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. P-0001" className="font-mono-num uppercase" data-testid="resume-code-input" />
          <Button variant="secondary" className="rounded-full font-bold" onClick={resume} disabled={resuming} data-testid="resume-code-btn">
            {resuming ? <Loader2 className="w-5 h-5 animate-spin" /> : "Start"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ZoneCard({ z, onPlay, event }) {
  const I = getIcon(z.icon);
  const statusMap = {
    available: { t: "Available", c: "text-neon-green", i: null },
    in_progress: { t: "In Progress", c: "text-gold", i: Clock },
    completed: { t: "Completed", c: "text-neon-blue", i: CheckCircle2 },
    locked: { t: "Locked", c: "text-muted-foreground", i: Lock },
  };
  const s = statusMap[z.status] || statusMap.available;
  const digital = z.mode === "digital";
  return (
    <div className="brut lift p-4 overflow-hidden relative" data-testid={`zone-card-${z.zone_id}`}>
      <div className={`absolute -right-6 -top-6 w-20 h-20 rounded-full bg-gradient-to-br ${gameGradient(z.game_type)} opacity-30 blur-xl`} />
      <div className="flex items-start gap-3">
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gameGradient(z.game_type)} grid place-items-center border-2 border-neutral-300 shrink-0`}><I className="w-6 h-6 text-white" /></div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-head font-bold text-lg truncate">{z.name}</h3>
            <span className={`text-xs font-semibold ${s.c} flex items-center gap-1`}>{s.i && <s.i className="w-3.5 h-3.5" />}{s.t}</span>
          </div>
          <p className="text-sm text-muted-foreground line-clamp-2">{z.description}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground font-mono-num">
            <span className="text-gold">+{z.max_xp} {term(event, "xp", "Cash")}</span>
            <span>· {z.est_time}</span>
            <span>· {z.attempts_used}/{z.attempts} tries</span>
          </div>
        </div>
      </div>
      <Button size="sm" className="w-full rounded-full mt-3 font-bold" variant={z.status === "completed" ? "secondary" : "default"}
        disabled={z.status === "locked" || (digital && z.attempts_used >= z.attempts)}
        onClick={() => digital ? onPlay(z) : toast.info(`Head to ${z.location || z.name} — a Game Master runs this one!`)}
        data-testid={`play-${z.zone_id}`}>
        {z.status === "locked" ? "Locked" : digital ? (z.status === "completed" ? "Play Again" : "Play Now") : "Visit Booth"}
      </Button>
    </div>
  );
}

function HomeTab({ event, zones, onPlay, achievements, txns, xpLabel }) {
  const unlocked = achievements.filter((a) => a.unlocked);
  return (
    <div className="space-y-6">
      {unlocked.length > 0 && (
        <div>
          <h2 className="font-head font-bold text-lg mb-3">My Achievements</h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
            {unlocked.map((a) => { const I = getIcon(a.icon); return (
              <div key={a.key} className="brut-hard bg-card p-3 rounded-xl min-w-[92px] text-center shrink-0 animate-pop">
                <I className="w-6 h-6 mx-auto text-gold mb-1" /><p className="text-xs font-semibold">{a.name}</p>
              </div>
            ); })}
          </div>
        </div>
      )}
      <div className="brut p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="font-head font-bold">Your progress</p>
          <span className="font-mono-num text-sm text-muted-foreground" data-testid="home-progress">{zones.filter((z) => z.status === "completed").length}/{zones.length} challenges</span>
        </div>
        <div className="h-3 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${zones.length ? Math.round(zones.filter((z) => z.status === "completed").length / zones.length * 100) : 0}%`, background: event.theme }} /></div>
      </div>
      {zones.filter((z) => z.status === "available" || z.status === "in_progress").length > 0 && (
        <div>
          <h2 className="font-head font-bold text-lg mb-3">Jump back in</h2>
          <div className="space-y-3">{zones.filter((z) => z.status === "available" || z.status === "in_progress").slice(0, 3).map((z) => <ZoneCard key={z.zone_id} z={z} onPlay={onPlay} event={event} />)}</div>
          <p className="text-xs text-muted-foreground mt-3 text-center">See every challenge in the {term(event, "zone", "Challenge Zones")} tab →</p>
        </div>
      )}
      {txns.length > 0 && (
        <div>
          <h2 className="font-head font-bold text-lg mb-3 flex items-center gap-2"><History className="w-4 h-4" />My Activity</h2>
          <div className="space-y-2">
            {txns.slice(0, 8).map((t) => (
              <div key={t.txn_id} className="brut p-3 flex items-center justify-between text-sm">
                <div><p className="font-semibold">{t.zone_name || "Bonus"}</p><p className="text-xs text-muted-foreground">{t.type.replace("_", " ")} · {new Date(t.created_at).toLocaleTimeString()}</p></div>
                <span className="font-mono-num text-neon-green font-bold">+{t.xp}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-center text-xs text-muted-foreground pt-2" data-testid="player-contact">Questions? Contact {event.organizer_name || "the organizer"} · <a href="mailto:support@arenafy.app" className="text-primary">support@arenafy.app</a></p>
    </div>
  );
}

function ZonesTab({ event, zones, onPlay }) {
  return <div className="space-y-3"><h2 className="font-head font-bold text-lg">{term(event, "zone", "Challenge Zones")}</h2>{zones.map((z) => <ZoneCard key={z.zone_id} z={z} onPlay={onPlay} event={event} />)}</div>;
}

function HofTab({ event, leaderboard, playerId, slug }) {
  const [teamMode, setTeamMode] = useState(false);
  const [rows, setRows] = useState(leaderboard);
  useEffect(() => {
    if (teamMode) api.get(`/public/events/${slug}/leaderboard?team=true`).then((r) => setRows(r.data));
    else setRows(leaderboard);
  }, [teamMode, leaderboard, slug]);
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-head font-black text-xl flex items-center gap-2"><Trophy className="w-5 h-5 text-gold" />{term(event, "leaderboard", "Hall of Fame")}</h2>
        {event.teams_enabled && (
          <div className="flex rounded-full border border-border overflow-hidden text-xs">
            <button onClick={() => setTeamMode(false)} className={`px-3 py-1.5 ${!teamMode ? "bg-primary text-white" : ""}`} data-testid="lb-individual">Players</button>
            <button onClick={() => setTeamMode(true)} className={`px-3 py-1.5 ${teamMode ? "bg-primary text-white" : ""}`} data-testid="lb-teams">Teams</button>
          </div>
        )}
      </div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className={`brut p-3 flex items-center gap-3 ${r.player_id === playerId ? "border-primary bg-primary/5" : ""}`} data-testid={`lb-row-${i}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center font-mono-num font-bold ${i < 3 ? "bg-gold text-black" : "bg-secondary"}`}>{r.rank}</div>
            <div className="flex-1"><p className="font-semibold">{r.name}</p>{r.team_name && <p className="text-xs text-muted-foreground">{r.team_name}</p>}{r.members != null && <p className="text-xs text-muted-foreground">{r.members} members</p>}</div>
            <span className="flex items-center gap-1 font-mono-num font-bold text-cash"><Coins className="w-4 h-4" />{r.xp}</span>
          </div>
        ))}
        {rows.length === 0 && <p className="text-center text-muted-foreground py-10">No scores yet — be the first!</p>}
      </div>
    </div>
  );
}

function RewardsTab({ rewards, event }) {
  if (!rewards) return <div className="grid place-items-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const tierColor = { participation: "#A3A3A3", bronze: "#CD7F32", silver: "#C0C0C0", gold: "#FFCC00" };
  return (
    <div>
      <h2 className="font-head font-black text-xl mb-1">{term(event, "rewards", "Reward Vault")}</h2>
      <p className="text-sm text-muted-foreground mb-4">Your score: <span className="font-mono-num text-gold">{rewards.xp} Cash</span></p>
      <div className="space-y-3">
        {rewards.rewards.map((r) => (
          <div key={r.reward_id} className={`brut p-4 flex items-center gap-4 ${r.eligible ? "border-gold" : r.unlocked ? "" : "opacity-60"}`} data-testid={`reward-${r.reward_id}`}>
            <div className="w-12 h-12 rounded-xl grid place-items-center border-2 border-neutral-300" style={{ background: tierColor[r.tier] || "#888" }}><Gift className="w-6 h-6 text-black" /></div>
            <div className="flex-1">
              <p className="font-head font-bold">{r.name}</p>
              <p className="text-xs text-muted-foreground font-mono-num">{r.min_xp}–{r.max_xp === 999999 ? "∞" : r.max_xp} Cash</p>
            </div>
            {r.collected ? <span className="text-xs font-bold text-neon-green flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />Collected</span>
              : r.eligible ? <span className="text-xs font-bold text-gold flex items-center gap-1"><Star className="w-4 h-4" />Unlocked!</span>
              : r.unlocked ? <span className="text-xs text-muted-foreground">Reached</span>
              : <span className="text-xs text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" />Locked</span>}
          </div>
        ))}
        {rewards.rewards.length === 0 && <p className="text-center text-muted-foreground py-10">No rewards configured yet.</p>}
      </div>
    </div>
  );
}

function extractToken(text) {
  if (!text) return "";
  try { const u = new URL(text); const vc = u.searchParams.get("vc"); if (vc) return vc; } catch (e) {}
  const m = String(text).match(/vc=([A-Za-z0-9]+)/);
  if (m) return m[1];
  return String(text).trim();
}

function CameraScanner({ onResult, onCancel }) {
  useEffect(() => {
    const h = new Html5Qrcode("qr-reader");
    let stopped = false;
    const stop = () => { try { return h.stop().then(() => h.clear()).catch(() => {}); } catch (e) {} };
    h.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
      (text) => { if (stopped) return; stopped = true; stop(); onResult(text); },
      () => {}
    ).catch(() => { toast.error("Camera unavailable — please enter the code manually"); onCancel && onCancel(); });
    return () => { stopped = true; stop(); };
  }, []); // eslint-disable-line
  return <div id="qr-reader" className="w-full rounded-xl overflow-hidden border-2 border-neutral-300" data-testid="qr-reader" />;
}
